import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import '../WeddingDay/WeddingDay.css';
import './WhatToExpect.css';
import Loader from '../Loader/Loader';
import Toast from '../Toast/Toast';
import { INVALID_LINK, PHONE_MAX_PX } from '../constants';
import { useDevice } from '../hooks/useDevice';
import { openExternalApp } from '../utils/androidUtils';
import { brandName, isAdminBuild, logoUrl } from '../WeddingDay/runtime';
import { TickBox } from '../WeddingDay/components/controls';
import { fetchFaq, saveFaq } from './api';
import { COVER, partsFor } from './content';

const e = React.createElement;

const ackId = (part, topic) => `${part.key}.${topic.key}`;

// The ticked topics as the server holds them.
const ticksFrom = (data) => {
  const ticked = {};
  Object.keys(data.acks || {}).forEach((key) => {
    if (data.acks[key].read) ticked[key] = true;
  });
  return ticked;
};

const topicCount = (parts) => parts.reduce((n, part) => n + part.topics.length, 0);

// Every topic in booklet order, with the title and part it was shown under
// and whether it is ticked, so ops reads back exactly what the couple saw.
const payloadOf = (parts, ticked) =>
  parts.flatMap((part) =>
    part.topics.map((topic) => ({
      key: ackId(part, topic),
      title: topic.title,
      part: part.title,
      read: !!ticked[ackId(part, topic)],
    })));

const Arrow = ({ dir }) =>
  e(
    'svg',
    {
      width: 16,
      height: 16,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 2.2,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    e('path', { d: dir === 'prev' ? 'M15 18l-6-6 6-6' : 'M9 6l6 6-6 6' })
  );

// One mark per row, so the two columns read apart without colouring
// whole boxes.
const Mark = ({ yes }) =>
  e(
    'span',
    { className: `wte-mark ${yes ? 'is-yes' : 'is-no'}`, 'aria-hidden': 'true' },
    e(
      'svg',
      {
        width: 10,
        height: 10,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 3.4,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
      },
      e('path', { d: yes ? 'M20 6L9 17l-5-5' : 'M18 6L6 18M6 6l12 12' })
    )
  );

const List = ({ yes, title, items }) =>
  e('div', { className: `wte-list ${yes ? 'is-yes' : 'is-no'}` }, [
    e('h4', { className: 'wte-list-head', key: 'h' }, title),
    e('ul', { className: 'wte-items', key: 'l' }, items.map((item, i) =>
      e('li', { className: 'wte-item', key: i }, [
        e(Mark, { yes, key: 'm' }),
        e('span', { key: 't' }, item),
      ]))),
  ]);

const Faq = ({ faq }) =>
  e('div', { className: 'wte-faq' }, [
    e('h4', { className: 'wte-faq-q', key: 'q' }, faq.q),
    e('p', { className: 'wte-faq-a', key: 'a' }, faq.a),
    faq.points
      ? e('ul', { className: 'wte-points', key: 'p' },
          faq.points.map((point, i) => e('li', { key: i }, point)))
      : null,
  ]);

// A link out to sample reels. The arrow nudges along so it reads as a link
// rather than as a label.
//
// Opened through openExternalApp: inside the Flutter app a target=_blank link
// does nothing, so the URL is handed to the app to open. The href stays, so it
// still reads and long-presses as a link everywhere.
const LinkOut = ({ link }) =>
  e('a', {
    className: 'wte-link',
    href: link.url,
    target: '_blank',
    rel: 'noopener noreferrer',
    onClick: (ev) => {
      ev.preventDefault();
      openExternalApp(link.url);
    },
  }, [
    // Drawn rather than typed: the font's own arrow is too small to read.
    e('svg', {
      key: 'arrow',
      className: 'wte-link-arrow',
      width: 18,
      height: 18,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 2.4,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      'aria-hidden': 'true',
    }, e('path', { d: 'M5 12h14M13 6l6 6-6 6' })),
    // The text alone carries the underline, so it is its own element.
    e('span', { className: 'wte-link-label', key: 'label' }, link.label),
    // The usual "opens elsewhere" mark: the playlist opens in a new tab.
    e('svg', {
      key: 'out',
      className: 'wte-link-out',
      width: 14,
      height: 14,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 2.2,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      'aria-hidden': 'true',
    }, [
      e('path', { key: 'box', d: 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6' }),
      e('path', { key: 'arrow', d: 'M15 3h6v6M10 14L21 3' }),
    ]),
  ]);

// A paragraph, or several where the copy gives an array.
const paras = (text, className, key) =>
  (Array.isArray(text) ? text : [text]).map((line, i) =>
    e('p', { className, key: `${key}${i}` }, line));

const TopicCard = ({ topic, acked }) =>
  e('section', { className: `wd-card wte-topic${acked ? ' is-read' : ''}` }, [
    e('span', { className: 'wd-flabel wte-topic-head', key: 'h' }, topic.title),
    topic.intro && topic.introLink
      ? e('p', { className: 'wte-topic-intro', key: 'i' }, [
          `${topic.intro} `,
          e(LinkOut, { key: 'link', link: topic.introLink }),
        ])
      : topic.intro ? paras(topic.intro, 'wte-topic-intro', 'i') : null,
    topic.introLinks
      ? e('span', { className: 'wte-links wte-intro-links', key: 'ilinks' },
          topic.introLinks.map((link) => e(LinkOut, { key: link.url, link })))
      : null,
    topic.note ? e('p', { className: 'wd-note', key: 'note' }, topic.note) : null,
    topic.body ? paras(topic.body, 'wte-topic-intro', 'b') : null,
    topic.points
      ? e('ul', { className: 'wte-points wte-topic-points', key: 'pts' },
          topic.points.map((point, i) => e('li', { key: i }, point)))
      : null,
    topic.expect || topic.notExpect
      ? e('div', { className: 'wte-lists', key: 'lists' }, [
          topic.expect
            ? e(List, { key: 'y', yes: true, title: 'What to expect', items: topic.expect })
            : null,
          topic.notExpect
            ? e(List, { key: 'n', yes: false, title: 'What not to expect', items: topic.notExpect })
            : null,
        ])
      : null,
    topic.faqs
      ? e('div', { className: 'wte-faqs', key: 'faqs' },
          topic.faqs.map((faq, i) => e(Faq, { faq, key: i })))
      : null,
    topic.footnote
      ? e('p', { className: 'wd-note', key: 'f' }, [
          topic.footnote,
          topic.footnoteLinks
            ? e('span', { className: 'wte-links', key: 'links' },
                topic.footnoteLinks.map((link) => e(LinkOut, { key: link.url, link })))
            : null,
        ])
      : null,
  ]);

// One tick for the whole section. It still saves a read against every topic
// in it, so ops reads the booklet back topic by topic as before.
const SectionAck = ({ acked, onToggle, disabled }) =>
  e(
    'button',
    {
      type: 'button',
      className: `wd-ackrow wte-ack${acked ? ' is-on' : ''}`,
      disabled,
      onClick: onToggle,
      'aria-pressed': String(acked),
    },
    [
      e(TickBox, { checked: acked, key: 't' }),
      e('span', { key: 'l' }, 'I have read and understood this section'),
    ]
  );

/**
 * The "What to Expect" booklet as a form, in the wedding day form's own
 * frame: the same header rail, cards and bottom bar. Every topic is ticked
 * before it can be submitted. The couple lands here once their wedding day
 * details are in.
 *
 * `chrome` is the host app's navbar and `useStoreToken` its token bootstrap,
 * as for the wedding day form.
 */
const WhatToExpect = ({ chrome, useStoreToken, navSelector = '.navbar' }) => {
  const { booking_id: bookingId } = useParams();
  const [params] = useSearchParams();
  // The same brief uuid the wedding day form was opened with.
  const briefUuid = params.get('brief') || '';
  // Login as User signs ops in with the couple's own token, so the server
  // reads them as the couple. Only the build says ops is the one reading,
  // as for the wedding day form, so nothing is saved from it.
  const readOnly = isAdminBuild() || params.get('readonly') === 'true';
  const { storeToLocal } = useStoreToken();
  const { isPhone, narrow } = useDevice(PHONE_MAX_PX);
  const [acks, setAcks] = useState({});
  const [bookingName, setBookingName] = useState('');
  // Which reel shooter the package has, so the matching reels topic shows.
  const [reelShooter, setReelShooter] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [busy, setBusy] = useState(false);
  // -1 is the cover page, before the first section.
  const [index, setIndex] = useState(-1);
  // Saves go out one after another, so a quick run of ticks lands on the
  // server in the order it was made.
  const queue = useRef(Promise.resolve());
  // Bumped on every tick, so a server read queued before a later tick is not
  // laid over it.
  const edits = useRef(0);

  useLayoutEffect(() => {
    storeToLocal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!briefUuid) {
      setLoading(false);
      return undefined;
    }
    let live = true;
    fetchFaq({ bookingId, briefUuid })
      .then((data) => {
        if (!live) return;
        const saved = ticksFrom(data);
        setAcks(saved);
        setBookingName(data.booking_name || '');
        const shooter = { reelShooter: data.reel_shooter || 'none' };
        setReelShooter(shooter);
        setSubmitted(data.status === 'submitted');
        // Opening the page is itself recorded, with the booklet as it now
        // stands, so ops can tell a couple who never came from one who
        // came and ticked nothing. Never from a read-only view: ops opening
        // the booking would otherwise be recorded as the couple.
        const shown = partsFor({ ...shooter, brand: brandName() });
        // On the same queue as the ticks, so a section ticked before this
        // lands is saved after it rather than overwritten by it. Not through
        // send(): that reads `parts` from state, which still holds the
        // booklet from before this response decided the reels variant.
        if (!readOnly && data.status !== 'submitted'
            && data.topics_total !== topicCount(shown)) {
          const run = queue.current.then(() =>
            saveFaq({ bookingId, briefUuid, topics: payloadOf(shown, saved) }));
          queue.current = run.catch(() => {});
        }
      })
      .catch(() => live && setLoadFailed(true))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [bookingId, briefUuid]);

  // The navbar has an auto height, measured the same way the wedding day
  // form does it.
  useEffect(() => {
    const nav = document.querySelector(navSelector);
    const sync = () => {
      if (nav) document.documentElement.style.setProperty('--wd-nav', `${nav.offsetHeight}px`);
    };
    sync();
    const observer = nav && window.ResizeObserver ? new ResizeObserver(sync) : null;
    if (observer) observer.observe(nav);
    window.addEventListener('resize', sync);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [navSelector]);

  const parts = useMemo(
    () => partsFor({ ...reelShooter, brand: brandName() }), [reelShooter]);

  const steps = useMemo(
    () =>
      parts.map((part, i) => {
        const done = part.topics.filter((t) => acks[ackId(part, t)]).length;
        return {
          key: part.key,
          label: part.label,
          index: i,
          fill: done / part.topics.length,
          done: done === part.topics.length,
          current: i === index,
        };
      }),
    [parts, acks, index]
  );
  const pending = steps.filter((s) => !s.done);
  const allComplete = pending.length === 0;
  const onCover = index < 0;
  const part = parts[index];
  const isLast = index === parts.length - 1;

  const send = (next, submit) => {
    const run = queue.current.then(() =>
      saveFaq({ bookingId, briefUuid, topics: payloadOf(parts, next), submit }));
    queue.current = run.catch(() => {});
    return run;
  };

  const toggle = (ids, on) => {
    if (readOnly) return;
    edits.current += 1;
    const next = { ...acks };
    ids.forEach((id) => {
      if (on) next[id] = true;
      else delete next[id];
    });
    setAcks(next);
    setSaveError(null);
    send(next, false).catch((err) => {
      setSaveError(err.message || 'Could not save that. Please try again.');
      // Saves queued behind this one carry its tick in their snapshot, so
      // neither restoring the old ticks nor undoing this one alone is sure
      // to match the server. Once the queue drains, show what it holds.
      const seen = edits.current;
      queue.current = queue.current
        .then(() => fetchFaq({ bookingId, briefUuid }))
        .then((data) => {
          if (edits.current === seen) setAcks(ticksFrom(data));
        })
        .catch(() => {});
    });
  };

  const submit = () => {
    if (readOnly) return;
    setBusy(true);
    setSaveError(null);
    send(acks, true)
      .then(() => setSubmitted(true))
      .catch((err) => setSaveError(err.message || 'Could not submit. Please try again.'))
      .finally(() => setBusy(false));
  };

  // A section is only left forward once it is ticked, so no section can be
  // reached past one that was skipped. Going back is always open.
  const reachable = (i) =>
    submitted || readOnly || steps.slice(0, Math.max(i, 0)).every((s) => s.done);

  const goTo = (i) => {
    if (!reachable(i)) return;
    setIndex(i);
    const content = document.querySelector('.wte-screen .wd-content');
    if (content) content.scrollTop = 0;
  };

  if (loading) return e(Loader, { logoSrc: logoUrl(), label: 'Loading' });
  if (!briefUuid) return e(Toast, { message: INVALID_LINK });
  if (loadFailed) {
    return e(
      'div',
      { className: 'wd-access' },
      e('div', { className: 'wd-access-box' }, [
        e('h1', { className: 'wd-access-head', key: 'h' }, 'We could not load this page'),
        e('p', { className: 'wd-access-body', key: 'b' }, 'Check your connection and try again.'),
        e('button', {
          key: 'retry',
          type: 'button',
          className: 'wd-btn wd-btn-brand',
          onClick: () => window.location.reload(),
        }, 'Try again'),
      ])
    );
  }

  const gap = isPhone ? 8 : 14;
  const here = steps[index];
  const at = Math.max(index, 0);
  const markerLeft = `calc((100% - ${gap * (steps.length - 1)}px) / ${steps.length}`
    + ` * ${(at + (here ? here.fill : 0)).toFixed(4)} + ${gap * at}px)`;
  const positionLabel = onCover ? 'Introduction' : `Section ${index + 1} of ${steps.length}`;
  // A phone's rail has no room for the step names, so the header names the
  // one they are on.
  const headerLabel = isPhone && !onCover
    ? `${index + 1} of ${steps.length} · ${part.label}`
    : positionLabel;

  const header = e('div', { className: `wd-toprail${isPhone ? ' is-phone' : ''}`, key: 'rail' }, [
    e('div', { className: 'wd-head', key: 'head' }, [
      e('span', { className: 'wd-eyebrow', key: 'eyebrow' }, 'WHAT TO EXPECT'),
      bookingName ? e('span', { className: 'wd-eyebrow-name', key: 'name' }, bookingName) : null,
      e('span', { className: 'wd-status', key: 'status' }, headerLabel.toUpperCase()),
    ]),
    e(
      'div',
      { className: 'wd-steps', key: 'steps' },
      [e('span', { className: 'wd-marker', key: 'marker', style: { left: markerLeft } })].concat(
        steps.map((step) =>
          e(
            'button',
            {
              key: step.key,
              type: 'button',
              className: `wd-step${step.current ? ' is-current' : ''}`,
              onClick: () => goTo(step.index),
            },
            [
              e('span', { className: 'wd-row', key: 'row' }, [
                e(
                  'span',
                  {
                    key: 'd',
                    className: `wd-dot ${step.done ? 'is-done' : step.current ? 'is-current' : 'is-ahead'}`,
                  },
                  step.done ? '✓' : String(step.index + 1)
                ),
                e('span', { className: 'wd-label', key: 'l' }, step.label),
              ]),
              e(
                'span',
                { className: 'wd-track', key: 't', title: `${step.label}: ${Math.round(step.fill * 100)}% read` },
                e('span', { className: 'wd-tseg' }, [
                  e('i', {
                    key: 'past',
                    className: 'wd-tseg-past',
                    style: { width: step.index < index ? '100%' : '0%' },
                  }),
                  e('i', {
                    key: 'fill',
                    className: `wd-tseg-fill${step.done ? ' is-done' : ''}`,
                    style: { width: `${Math.round(step.fill * 100)}%` },
                  }),
                ])
              ),
            ]
          )
        )
      )
    ),
  ]);

  const submitRow = isLast
    ? e('div', { className: 'wd-submitrow', key: 'submit' }, [
        allComplete || submitted || readOnly
          ? null
          : e('p', { className: 'wte-open', key: 'open' },
              `Still open: ${pending.map((s) => s.label).join(', ')}. Tick every section to submit.`),
        e(
          'button',
          {
            key: 'btn',
            type: 'button',
            className: 'wd-btn wd-btn-brand wd-btn-submit',
            disabled: readOnly || submitted || busy || !allComplete,
            onClick: submit,
          },
          submitted ? 'Submitted' : 'Submit'
        ),
        // Said once it is in, as the confirmation the grey button alone is not.
        submitted && !readOnly
          ? e('p', { className: 'wd-thanks', key: 'thanks' }, 'Thank you. Your answers are in.')
          : null,
      ])
    : null;

  return e(React.Fragment, null, [
    chrome ? e(React.Fragment, { key: 'chrome' }, chrome) : null,
    e(
      'div',
      {
        key: 'screen',
        className: `wd-screen wte-screen${isPhone ? ' is-phone' : ''}${narrow ? ' is-narrow' : ''}`,
      },
      [
        header,
        e('div', { className: 'wd-body', key: 'body' },
          e('main', { className: 'wd-content' }, [
            readOnly
              ? e('div', { className: 'wd-readonly', key: 'ro' }, 'Read only view of the client’s What to Expect page.')
              : null,
            saveError ? e('div', { className: 'wd-savewarn', key: 'err' }, saveError) : null,
            onCover ? e('div', { className: 'wd-wrap wte-cover', key: 'cover' },
              e('div', { className: 'wd-card wte-cover-card' }, [
                e('p', { className: 'wte-cover-sub', key: 'sub' }, COVER.subtitle),
                e('h1', { className: 'wte-cover-title', key: 'title' },
                  brandName() ? `${brandName()}: ${COVER.title}` : COVER.title),
                paras(COVER.body, 'wte-cover-body', 'body'),
                // What the sections are, so the couple knows how much is ahead.
                e('ol', { className: 'wte-cover-parts', key: 'parts' },
                  parts.map((p, i) => e('li', { key: p.key }, [
                    e('span', { className: 'wte-cover-num', key: 'n' }, String(i + 1)),
                    e('span', { key: 't' }, p.title),
                  ]))),
                e('button', {
                  key: 'start',
                  type: 'button',
                  className: 'wd-btn wd-btn-brand wte-cover-start',
                  onClick: () => goTo(0),
                }, 'Start reading'),
              ])) : e('div', { className: 'wd-wrap', key: 'wrap' }, [
              e('h1', { className: 'wd-sec', key: 'title' }, part.title),
              part.intro ? paras(part.intro, 'wd-intro', 'intro') : null,
              part.topics.map((topic) => e(TopicCard, {
                key: ackId(part, topic),
                topic,
                acked: here.done,
              })),
              e(SectionAck, {
                key: 'ack',
                acked: here.done,
                disabled: submitted || readOnly,
                onToggle: () => toggle(
                  part.topics.map((topic) => ackId(part, topic)), !here.done),
              }),
              submitRow,
            ]),
          ])
        ),
        e('div', { className: 'wd-bar', key: 'bar' }, [
          e(
            'button',
            {
              key: 'prev',
              type: 'button',
              className: 'wd-nav',
              disabled: onCover,
              onClick: () => goTo(index - 1),
              'aria-label': 'Previous section',
            },
            e(Arrow, { dir: 'prev' })
          ),
          e('div', { className: 'wd-bar-status', key: 'status' }, positionLabel),
          e(
            'button',
            {
              key: 'next',
              type: 'button',
              className: 'wd-nav',
              disabled: isLast || !reachable(index + 1),
              onClick: () => goTo(index + 1),
              'aria-label': 'Next section',
              title: isLast || reachable(index + 1)
                ? undefined
                : 'Tick this section to continue',
            },
            e(Arrow, { dir: 'next' })
          ),
        ]),
      ]
    ),
  ]);
};

export default WhatToExpect;
